/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Text, Tags, ToggleText, Spacer, Loader, TimeSquare, QuestionMark, IconComponent,
} from '@myinterview/component-library';
import { urlRegex } from '../utils/regex';
import { SupportedLinkDescription } from './SupportedLinkDescription';
import { IJob } from '../interfaces/jobInterface';
import { emitTrackEvent, EVENT_TYPES } from '../services/eventService';

const experienceMap = new Map([
  ['0-2 years', 'years_zero_two'],
  ['3-5 years', 'years_three_five'],
  ['5+ years', 'years_five_plus'],
]);

interface IDescription {
  candidateName?: string;
  jobDetails?: IJob;
  isLoading: boolean;
}

export const JobDescription = ({
  candidateName,
  jobDetails,
  isLoading,
}: IDescription) => {
  const [showMore, setShowMore] = useState(false);
  const [isLongDescription, setIsLongDescription] = useState(false);
  const [jobDescriptionText, setJobDescriptionText] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    let textToSet = t('form.introduction');
    const isLong = (jobDetails?.jobDescription?.length || 0) > 250;
    if (jobDetails?.jobDescription?.length) {
      textToSet = isLong && !showMore ? `${jobDetails?.jobDescription?.substring(0, 250)}...` : jobDetails?.jobDescription;
      setIsLongDescription(isLong);
    }
    setJobDescriptionText(textToSet);
  }, [jobDetails?.jobDescription, showMore, t]);

  const handleShowMore = (val: boolean) => {
    setShowMore(val);
    emitTrackEvent({ eventType: val ? EVENT_TYPES.SHOW_MORE : EVENT_TYPES.SHOW_LESS });
  };

  const supportedLinkDescription = useMemo(() => {
    const urls = jobDescriptionText.match(urlRegex);
    const descriptionArr = jobDescriptionText.split(urlRegex);
    return descriptionArr.map((text, idx) => (
      <SupportedLinkDescription
        key={idx} // same text create duplications when show more / show less so we use the index
        text={text}
        link={urls?.[idx] ? urls[idx] : ''}
      />
    ));
  }, [jobDescriptionText]);
  // eslint-disable-next-line max-len
  const interviewDuration = () => {
    // eslint-disable-next-line max-len
    const duration = jobDetails?.questions?.reduce((prev, curr) => prev + curr.partDuration, 0) || 0;
    return Math.round(duration / 60);
  };
  const numQuestions = () => jobDetails?.questions?.length || 0;
  return (
    <div className="job-description dynamic-direction">
      <Spacer y={1} />
      {isLoading ? <Loader count={1} /> : (
        <Text size="H2-Bold">
          {t('form.welcome')}
          {candidateName ? ` ${candidateName}` : ''}
          !
        </Text>
      )}
      <Spacer y={1} />
      {isLoading ? <Loader count={5} />
        : (
          <div>
            <Text className="job-description__welcome-message" size="S-Regular" color="neutral-60">{supportedLinkDescription}</Text>
            {isLongDescription && (
            <>
              <Spacer y={0.5} />
              <div className="job-description__toggle">
                <ToggleText
                  openedText={t('form.see_less')}
                  closedText={t('form.see_more')}
                  onToggle={handleShowMore}
                />
              </div>
            </>
            )}
          </div>
        )}
      <Spacer y={1} />
      {!!(interviewDuration() || numQuestions()) && (
      <div className="job-description__job-details">
        {!!interviewDuration() && (
        <div className="job-description__job-details__item">
          <IconComponent size="16px" icon={<TimeSquare />} />
          <Text className="job-description__job-details__item__text" size="S-Regular">
            {`${interviewDuration()} ${t('form.minute')}`}
          </Text>
        </div>
        )}
        <div className="job-description__job-details__dot-separator" />
        {numQuestions() && (
        <div className="job-description__job-details__item">
          <IconComponent className="job-description__job-details__item" size="16px" icon={<QuestionMark />} />
          <Text className="job-description__job-details__item__text" size="S-Regular">
            {`${numQuestions()} ${t('form.question', { count: numQuestions() })}`}
          </Text>
        </div>
        )}
      </div>
      )}
      <Spacer y={1} />
      {!!jobDetails?.skills?.length && (
      <>
        <Text size="H3-Semibold">{t('form.skills')}</Text>
        <div>{jobDetails.skills.map((skill) => <Tags key={skill}>{skill}</Tags>)}</div>
        <Spacer y={1} />
      </>
      )}
      {jobDetails?.experience && (
      <>
        <Text size="H3-Semibold">{t('form.experience')}</Text>
        <Text size="M-Regular">{t(`form.experience_years.${experienceMap.get(jobDetails.experience)!}`)}</Text>
        <Spacer y={1} />
      </>
      )}
    </div>
  );
};
